import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc, addDoc, setDoc, serverTimestamp, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { 
  Music, 
  Calendar, 
  DollarSign, 
  Edit, 
  Trash2, 
  Plus,
  Save,
  X,
  Clock,
  MapPin,
  User,
  Tag,
  AlertTriangle,
  UserX,
  ListMusic,
  RefreshCw,
  Sparkles,
  Radio
} from 'lucide-react';
import { writeBatch } from 'firebase/firestore';
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';
import { getMinimumPrice, formatMinimumPrice, getCurrencySymbol, getTestSongMinimumPrice } from '@/lib/currencyConfig';
import { ARTIST_ANALYTICS_EVENT_TYPES } from '@/lib/recordArtistAnalyticsEvent';
import AdminArtistDemoPanel from '@/features/admin/components/AdminArtistDemoPanel';
import { formatUkDate, formatUkDateTime } from '@/lib/adminFormat';

const AdminArtistManager = ({ artistId, onClose }) => {
  const { toast } = useToast();
  const [artist, setArtist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [gigs, setGigs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [setlists, setSetlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('songs');
  const [editingSong, setEditingSong] = useState(null);
  const [editingGig, setEditingGig] = useState(null);
  const [showAddSong, setShowAddSong] = useState(false);
  const [showAddGig, setShowAddGig] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('4.00');
  const [updatingBulkPrice, setUpdatingBulkPrice] = useState(false);
  const [forcingOnline, setForcingOnline] = useState(false);

  const artistCcy = useMemo(() => artist?.currency || DEFAULT_CURRENCY, [artist?.currency]);
  const fmt = (n) => formatCurrency(Number(n) || 0, artistCcy);

  useEffect(() => {
    if (artistId) {
      fetchArtistData();
    }
  }, [artistId]);

  const fetchArtistData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch artist profile
      const artistDocRef = doc(db, 'profiles', artistId);
      const artistDoc = await getDoc(artistDocRef);
      if (artistDoc.exists()) {
        const raw = artistDoc.data();
        let hasCompletedLiveTrialDemo = !!raw.trial_demo_completed_at;
        if (!hasCompletedLiveTrialDemo) {
          try {
            const evQ = query(
              collection(db, 'artist_analytics_events'),
              where('user_id', '==', artistId),
              where('event_type', '==', ARTIST_ANALYTICS_EVENT_TYPES.LIVE_TRIAL_DEMO_COMPLETED),
              limit(1)
            );
            const evSnap = await getDocs(evQ);
            hasCompletedLiveTrialDemo = !evSnap.empty;
          } catch (e) {
            console.warn('trial demo completion lookup:', e);
          }
        }
        const artistData = {
          id: artistDoc.id,
          ...raw,
          has_completed_live_trial_demo: hasCompletedLiveTrialDemo,
        };
        setArtist(artistData);
      } else {
        setError('Artist not found');
        return;
      }

      // Fetch songs
      const songsQuery = query(collection(db, 'songs'), where('user_id', '==', artistId));
      const songsSnapshot = await getDocs(songsQuery);
      const songsData = songsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSongs(songsData);

      // Fetch gigs
      const gigsQuery = query(collection(db, 'gigs'), where('user_id', '==', artistId));
      const gigsSnapshot = await getDocs(gigsQuery);
      const gigsData = gigsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGigs(gigsData);

      // Fetch requests - only show paid requests (with payment_intent_id)
      // "Mark As Played" requests (without payment_intent_id) should NOT be shown
      const requestsQuery = query(collection(db, 'requests'), where('artist_id', '==', artistId));
      const requestsSnapshot = await getDocs(requestsQuery);
      const requestsData = requestsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(request => request.payment_intent_id); // Only show paid requests
      setRequests(requestsData);

      // Fetch setlists (stored in playlists collection)
      const setlistsQuery = query(collection(db, 'playlists'), where('user_id', '==', artistId));
      const setlistsSnapshot = await getDocs(setlistsQuery);
      const setlistsData = setlistsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSetlists(setlistsData);

    } catch (error) {
      console.error('Error fetching artist data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPriceUpdate = async () => {
    const price = parseFloat(bulkPrice);
    if (isNaN(price) || price < 0) {
      toast({
        title: "Invalid Price",
        description: "Please enter a valid price",
        variant: "destructive"
      });
      return;
    }

    setUpdatingBulkPrice(true);
    try {
      const batch = writeBatch(db);
      let updatedCount = 0;

      songs.forEach((song) => {
        batch.update(doc(db, 'songs', song.id), {
          price: price
        });
        updatedCount++;
      });

      await batch.commit();
      
      toast({
        title: "Success!",
        description: `Updated ${updatedCount} song(s) to ${fmt(price)}`,
      });

      // Refresh songs list
      await fetchArtistData();
    } catch (error) {
      console.error('Error updating bulk prices:', error);
      toast({
        title: "Error",
        description: "Failed to update song prices",
        variant: "destructive"
      });
    } finally {
      setUpdatingBulkPrice(false);
    }
  };

  const handleForceArtistOnline = async () => {
    if (!artistId) return;
    const ok = window.confirm(
      'Force this artist LIVE with a new 4-hour session? Their public page will show LIVE immediately. They should end the session from their device when appropriate.'
    );
    if (!ok) return;
    setForcingOnline(true);
    try {
      const newSessionId = `session_admin_${artistId}_${Date.now()}`;
      const startTime = new Date();
      const endTime = new Date(startTime.getTime() + 4 * 60 * 60 * 1000);
      await setDoc(
        doc(db, 'live_status', artistId),
        {
          user_id: artistId,
          is_live: true,
          session_id: newSessionId,
          session_start_time: startTime,
          session_end_time: endTime,
          accepting_new_requests: true,
          request_cap: 5,
          active_playlist_id: null,
          updated_at: serverTimestamp(),
        },
        { merge: true }
      );
      toast({
        title: 'Artist forced online',
        description: 'A new live session was started (4h timer).',
      });
    } catch (error) {
      console.error('Force online:', error);
      toast({
        title: 'Could not force online',
        description: error?.message || 'Check permissions and try again.',
        variant: 'destructive',
      });
    } finally {
      setForcingOnline(false);
    }
  };

  const handleSongUpdate = async (songId, updates) => {
    try {
      // Ensure original_artist is set (use artist if original_artist not provided)
      const updatesToSave = {
        ...updates,
        original_artist: updates.original_artist || updates.artist || '',
        updated_at: new Date().toISOString()
      };
      // Remove artist field if it exists, use original_artist instead
      if (updatesToSave.artist && !updatesToSave.original_artist) {
        updatesToSave.original_artist = updatesToSave.artist;
      }
      delete updatesToSave.artist; // Remove artist field, use original_artist
      
      await updateDoc(doc(db, 'songs', songId), updatesToSave);
      await fetchArtistData();
      setEditingSong(null);
    } catch (error) {
      console.error('Error updating song:', error);
    }
  };

  const handleSongDelete = async (songId) => {
    if (window.confirm('Are you sure you want to delete this song?')) {
      try {
        await deleteDoc(doc(db, 'songs', songId));
        await fetchArtistData();
      } catch (error) {
        console.error('Error deleting song:', error);
      }
    }
  };

  const handleGigUpdate = async (gigId, updates) => {
    try {
      await updateDoc(doc(db, 'gigs', gigId), {
        ...updates,
        updated_at: new Date().toISOString()
      });
      await fetchArtistData();
      setEditingGig(null);
    } catch (error) {
      console.error('Error updating gig:', error);
    }
  };

  const handleGigDelete = async (gigId) => {
    if (window.confirm('Are you sure you want to delete this gig?')) {
      try {
        await deleteDoc(doc(db, 'gigs', gigId));
        await fetchArtistData();
      } catch (error) {
        console.error('Error deleting gig:', error);
      }
    }
  };

  const handleAddSong = async (songData) => {
    try {
      // Ensure original_artist is set (use artist if original_artist not provided)
      const songToSave = {
        ...songData,
        original_artist: songData.original_artist || songData.artist || '',
        user_id: artistId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      // Remove artist field if it exists, use original_artist instead
      if (songToSave.artist && !songToSave.original_artist) {
        songToSave.original_artist = songToSave.artist;
      }
      delete songToSave.artist; // Remove artist field, use original_artist
      
      await addDoc(collection(db, 'songs'), songToSave);
      await fetchArtistData();
      setShowAddSong(false);
      toast({
        title: "Success!",
        description: `Song "${songToSave.title}" by ${songToSave.original_artist} added successfully!`,
      });
    } catch (error) {
      console.error('Error adding song:', error);
      toast({
        title: "Error",
        description: "Failed to add song. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddGig = async (gigData) => {
    try {
      await addDoc(collection(db, 'gigs'), {
        ...gigData,
        user_id: artistId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      await fetchArtistData();
      setShowAddGig(false);
    } catch (error) {
      console.error('Error adding gig:', error);
    }
  };

  const handleDeleteUserAccount = async () => {
    if (!artistId) return;
    
    setDeleting(true);
    try {
      if (!auth.currentUser) {
        throw new Error('Admin session expired. Please sign in again.');
      }
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch('https://deleteuseraccounthttp-2j74a3qova-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ userId: artistId })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete account');
      }
      
      const result = await response.json();
      console.log('✅ Successfully deleted user:', result);
      
      toast({
        title: "✅ Account Deleted",
        description: `User account for ${artist?.artist_name || artist?.email} has been permanently deleted. The email can now be used for new signups.`,
      });
      
      // Close the modal and refresh the parent component
      onClose();
      
    } catch (error) {
      console.error('Error deleting user account:', error);
      toast({
        title: "❌ Deletion Failed",
        description: error.message || "Failed to delete user account. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-red-400">{error}</p>
        <Button onClick={onClose} variant="outline" className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-400">Loading artist data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Artist Header */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg sm:text-2xl">
                  {artist.artist_name?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <CardTitle className="text-white text-lg sm:text-xl">{artist.artist_name}</CardTitle>
                <CardDescription className="text-gray-400 text-sm">{artist.email}</CardDescription>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge variant="outline" className="border-blue-500 text-blue-400 text-xs">
                    {artist.account_type || 'artist'}
                  </Badge>
                  <Badge variant="outline" className="border-green-500 text-green-400 text-xs">
                    Active
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              <Button 
                onClick={() => setShowDeleteConfirm(true)} 
                variant="outline" 
                size="sm" 
                className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
              >
                <UserX className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
              <Button onClick={onClose} variant="outline" size="sm">
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-1 bg-gray-800 p-1 rounded-lg w-full sm:w-fit items-stretch sm:items-center">
        {[
          { id: 'songs', label: 'Songs', icon: Music, count: songs.length },
          { id: 'setlists', label: 'Setlists', icon: ListMusic, count: setlists.length },
          { id: 'gigs', label: 'Gigs', icon: Calendar, count: gigs.length },
          { id: 'requests', label: 'Requests', icon: DollarSign, count: requests.length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center justify-center sm:justify-start space-x-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm ${
              activeTab === tab.id
                ? 'bg-red-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
            {typeof tab.count === 'number' && (
              <Badge variant="outline" className="ml-1 text-xs">
                {tab.count}
              </Badge>
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={handleForceArtistOnline}
          disabled={forcingOnline}
          className={`flex items-center justify-center sm:justify-start space-x-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm border border-emerald-600/50 text-emerald-300 hover:bg-emerald-900/30 ${
            forcingOnline ? 'opacity-60 pointer-events-none' : ''
          }`}
        >
          {forcingOnline ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Radio className="w-4 h-4" />
          )}
          <span>Force online</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('demo')}
          className={`flex items-center justify-center sm:justify-start space-x-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'demo'
              ? 'bg-red-500 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          <span className="flex items-center gap-2">
            Demo
            {artist?.has_completed_live_trial_demo ? (
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-violet-400/70 bg-violet-600/35 text-[10px] font-bold text-violet-100"
                title="Completed first-time Live walkthrough (onboarding demo)"
              >
                D
              </span>
            ) : null}
          </span>
        </button>
      </div>

      {/* Demo Tab — client-side only, no Firestore / payments */}
      {activeTab === 'demo' && artist && (
        <AdminArtistDemoPanel
          artistId={artistId}
          artist={artist}
          songs={songs}
          artistCcy={artistCcy}
          fmt={fmt}
        />
      )}

      {/* Songs Tab */}
      {activeTab === 'songs' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-white">Song Catalogue</h3>
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              {/* Bulk Price Update */}
              <div className="flex items-center space-x-2 bg-gray-700 rounded-lg px-3 py-2 flex-1 sm:flex-initial">
                <span className="text-sm text-gray-300 whitespace-nowrap">Bulk Update:</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  className="w-20 h-8 bg-gray-600 border-gray-500 text-white text-sm"
                  placeholder="4.00"
                />
                <span className="text-sm text-gray-300">{getCurrencySymbol(artistCcy)}</span>
                <Button
                  size="sm"
                  onClick={handleBulkPriceUpdate}
                  disabled={updatingBulkPrice || songs.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 h-8"
                >
                  {updatingBulkPrice ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <DollarSign className="w-3 h-3 mr-1" />
                      Update All
                    </>
                  )}
                </Button>
              </div>
              <Button onClick={() => setShowAddSong(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Song
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {songs.map((song) => (
              <Card key={song.id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  {editingSong?.id === song.id ? (
                    <SongEditForm
                      song={editingSong}
                      currency={artistCcy}
                      onSave={handleSongUpdate}
                      onCancel={() => setEditingSong(null)}
                    />
                  ) : (
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-white">{song.title}</h4>
                          <p className="text-sm text-gray-400">{song.original_artist || song.artist}</p>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingSong(song)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSongDelete(song.id)}
                            className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Price:</span>
                          <span className="text-white font-medium">{fmt(song.price || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Tags:</span>
                          <div className="flex flex-wrap gap-1">
                            {song.tags?.slice(0, 2).map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {song.tags?.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{song.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {song.archived && (
                          <Badge variant="outline" className="border-orange-500 text-orange-400 text-xs">
                            Archived
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Setlists Tab */}
      {activeTab === 'setlists' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Setlists</h3>
          
          {setlists.length > 0 ? (
            <div className="space-y-4">
              {setlists.map((setlist) => (
                <Card key={setlist.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-2">{setlist.name || 'Unnamed Setlist'}</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center space-x-2">
                            <ListMusic className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-400">Songs:</span>
                            <span className="text-white">{setlist.songs?.length || 0}</span>
                          </div>
                          {setlist.created_at && (
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-400">Created:</span>
                              <span className="text-white">
                                {formatUkDate(setlist.created_at)}
                              </span>
                            </div>
                          )}
                          {setlist.description && (
                            <p className="text-gray-300 mt-2">{setlist.description}</p>
                          )}
                        </div>
                        {setlist.songs && setlist.songs.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <p className="text-xs text-gray-400 mb-2">Songs in this setlist:</p>
                            <div className="flex flex-wrap gap-2">
                              {setlist.songs.slice(0, 5).map((songId, index) => {
                                const song = songs.find(s => s.id === songId);
                                return song ? (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {song.title}
                                  </Badge>
                                ) : null;
                              })}
                              {setlist.songs.length > 5 && (
                                <Badge variant="outline" className="text-xs">
                                  +{setlist.songs.length - 5} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ListMusic className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No setlists found</p>
              <p className="text-sm text-gray-500 mt-2">This artist hasn't created any setlists yet</p>
            </div>
          )}
        </div>
      )}

      {/* Gigs Tab */}
      {activeTab === 'gigs' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Gigs & Events</h3>
            <Button onClick={() => setShowAddGig(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Gig
            </Button>
          </div>

          <div className="space-y-4">
            {gigs.map((gig) => (
              <Card key={gig.id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  {editingGig?.id === gig.id ? (
                    <GigEditForm
                      gig={editingGig}
                      onSave={handleGigUpdate}
                      onCancel={() => setEditingGig(null)}
                    />
                  ) : (
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-white">{gig.title}</h4>
                          <p className="text-sm text-gray-400">{gig.venue}</p>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingGig(gig)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGigDelete(gig.id)}
                            className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">Date:</span>
                          <span className="text-white">{formatUkDate(gig.date)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">Time:</span>
                          <span className="text-white">{gig.time || 'TBD'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">Location:</span>
                          <span className="text-white">{gig.location || 'TBD'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">Rate:</span>
                          <span className="text-white">{fmt(gig.rate || 0)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Song Requests</h3>
          
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-white">{request.song_title}</h4>
                      <p className="text-sm text-gray-400">by {request.song_artist}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm">
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">From:</span>
                          <span className="text-white">{request.requester_name || 'Anonymous'}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">Paid:</span>
                          <span className="text-white">{fmt(request.amount_paid || 0)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-400">Time:</span>
                          <span className="text-white">{formatUkDateTime(request.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={request.status === 'completed' ? 'border-green-500 text-green-400' : 'border-yellow-500 text-yellow-400'}
                    >
                      {request.status || 'pending'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add Song Modal */}
      <AnimatePresence>
        {showAddSong && (
          <AddSongModal
            currency={artistCcy}
            onSave={handleAddSong}
            onCancel={() => setShowAddSong(false)}
          />
        )}
      </AnimatePresence>

      {/* Add Gig Modal */}
      <AnimatePresence>
        {showAddGig && (
          <AddGigModal
            onSave={handleAddGig}
            onCancel={() => setShowAddGig(false)}
          />
        )}
      </AnimatePresence>

      {/* Delete User Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 rounded-lg max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Delete User Account</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-300">
                  Are you sure you want to permanently delete the account for{' '}
                  <span className="font-semibold text-white">
                    {artist?.artist_name || artist?.email}
                  </span>?
                </p>
                
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <p className="text-sm text-red-300">
                    <strong>This will permanently delete:</strong>
                  </p>
                  <ul className="text-sm text-red-300 mt-2 space-y-1">
                    <li>• User's Firebase Authentication account</li>
                    <li>• All songs, gigs, and requests</li>
                    <li>• Profile and user data</li>
                    <li>• Session history and push subscriptions</li>
                  </ul>
                  <p className="text-sm text-green-300 mt-2">
                    <strong>✅ The email address will be available for new signups</strong>
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button 
                  onClick={handleDeleteUserAccount}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 flex-1"
                >
                  {deleting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Delete Account
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="outline" 
                  className="flex-1"
                  disabled={deleting}
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Song Edit Form Component
const SongEditForm = ({ song, onSave, onCancel, currency = DEFAULT_CURRENCY }) => {
  const minRegular = getMinimumPrice(currency);
  const minLabel = formatMinimumPrice(currency);
  const testMin = getTestSongMinimumPrice(currency);
  const testLabel = formatCurrency(testMin, currency);
  const zeroLabel = formatCurrency(0, currency);
  const sym = getCurrencySymbol(currency);

  const [formData, setFormData] = useState({
    title: song.title || '',
    artist: song.original_artist || song.artist || '',
    price: song.price || 0,
    tags: song.tags?.join(', ') || ''
  });
  const [priceError, setPriceError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Validate price when title, artist, or price changes
      if (name === 'price' || name === 'title' || name === 'artist') {
        const priceNum = parseFloat(updated.price) || 0;
        const isFreeSong = updated.title.trim().toLowerCase() === 'free' && 
                          updated.artist.trim().toLowerCase() === 'free';
        const isTestSong = updated.title.trim().toLowerCase() === 'test' && 
                          updated.artist.trim().toLowerCase() === 'test';
        
        if (value === '' || (name === 'price' && isNaN(priceNum))) {
          setPriceError('');
        } else if (priceNum < 0) {
          setPriceError('Price cannot be negative.');
        } else if (isFreeSong && priceNum > 0) {
          setPriceError(`For "free" songs, price must be ${zeroLabel}.`);
        } else if (isFreeSong && priceNum === 0) {
          setPriceError(''); // Valid free song
        } else if (isTestSong && priceNum < testMin) {
          setPriceError(`Minimum price for test songs is ${testLabel}.`);
        } else if (!isTestSong && !isFreeSong && priceNum < minRegular) {
          setPriceError(`Minimum price is ${minLabel}.`);
        } else {
          setPriceError('');
        }
      }
      
      return updated;
    });
  };

  const handleSave = () => {
    const price = parseFloat(formData.price) || 0;
    const isFreeSong = formData.title.trim().toLowerCase() === 'free' && 
                      formData.artist.trim().toLowerCase() === 'free';
    const isTestSong = formData.title.trim().toLowerCase() === 'test' && 
                      formData.artist.trim().toLowerCase() === 'test';
    
    // Validate price
    if (price < 0) {
      setPriceError('Price cannot be negative.');
      return;
    }
    
    if (isFreeSong && price > 0) {
      setPriceError(`For "free" songs, price must be ${zeroLabel}.`);
      return;
    }
    
    if (!isFreeSong && !isTestSong && (isNaN(price) || price < minRegular)) {
      setPriceError(`Minimum price is ${minLabel}.`);
      return;
    }
    
    if (isTestSong && (isNaN(price) || price < testMin)) {
      setPriceError(`Minimum price for test songs is ${testLabel}.`);
      return;
    }
    
    const updates = {
      title: formData.title,
      original_artist: formData.artist, // Use original_artist field
      price: price,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
    };
    onSave(song.id, updates);
  };

  return (
    <div className="space-y-3">
      <Input
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        placeholder="Song Title"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <Input
        name="artist"
        value={formData.artist}
        onChange={handleInputChange}
        placeholder="Artist Name"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <div className="space-y-2">
        <label className="text-sm text-gray-300">
          Price ({sym}) *
        </label>
        <Input
          name="price"
          type="number"
          step="0.01"
          min="0"
          value={formData.price}
          onChange={handleInputChange}
          placeholder={String(minRegular)}
          className={`bg-gray-700 border-gray-600 text-white ${
            priceError ? 'border-red-500 focus:border-red-500' : ''
          }`}
        />
        <p className="text-xs text-gray-400">
          Minimum: {minLabel}. Use "free"/"free" for {zeroLabel} or "test"/"test" for {testLabel}.
        </p>
        {priceError && (
          <p className="text-sm text-red-400 mt-1">{priceError}</p>
        )}
      </div>
      <Input
        name="tags"
        value={formData.tags}
        onChange={handleInputChange}
        placeholder="Tags (comma separated)"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <div className="flex space-x-2">
        <Button size="sm" onClick={handleSave} className="bg-green-600 hover:bg-green-700">
          <Save className="w-3 h-3 mr-1" />
          Save
        </Button>
        <Button size="sm" onClick={onCancel} variant="outline">
          Cancel
        </Button>
      </div>
    </div>
  );
};

// Gig Edit Form Component
const GigEditForm = ({ gig, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: gig.title || '',
    venue: gig.venue || '',
    date: gig.date || '',
    time: gig.time || '',
    location: gig.location || '',
    rate: gig.rate || 0
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const updates = {
      ...formData,
      rate: parseFloat(formData.rate) || 0
    };
    onSave(gig.id, updates);
  };

  return (
    <div className="space-y-3">
      <Input
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        placeholder="Gig Title"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <Input
        name="venue"
        value={formData.venue}
        onChange={handleInputChange}
        placeholder="Venue"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <Input
        name="date"
        type="date"
        value={formData.date}
        onChange={handleInputChange}
        className="bg-gray-700 border-gray-600 text-white"
      />
      <Input
        name="time"
        value={formData.time}
        onChange={handleInputChange}
        placeholder="Time"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <Input
        name="location"
        value={formData.location}
        onChange={handleInputChange}
        placeholder="Location"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <Input
        name="rate"
        type="number"
        value={formData.rate}
        onChange={handleInputChange}
        placeholder="Rate"
        className="bg-gray-700 border-gray-600 text-white"
      />
      <div className="flex space-x-2">
        <Button size="sm" onClick={handleSave} className="bg-green-600 hover:bg-green-700">
          <Save className="w-3 h-3 mr-1" />
          Save
        </Button>
        <Button size="sm" onClick={onCancel} variant="outline">
          Cancel
        </Button>
      </div>
    </div>
  );
};

// Add Song Modal Component
const AddSongModal = ({ onSave, onCancel, currency = DEFAULT_CURRENCY }) => {
  const minRegular = getMinimumPrice(currency);
  const minLabel = formatMinimumPrice(currency);
  const testMin = getTestSongMinimumPrice(currency);
  const testLabel = formatCurrency(testMin, currency);
  const zeroLabel = formatCurrency(0, currency);
  const sym = getCurrencySymbol(currency);

  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    price: 0,
    tags: ''
  });
  const [priceError, setPriceError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Validate price when title, artist, or price changes
      if (name === 'price' || name === 'title' || name === 'artist') {
        // Get current values
        const titleLower = (updated.title || '').trim().toLowerCase();
        const artistLower = (updated.artist || '').trim().toLowerCase();
        const isFreeSong = titleLower === 'free' && artistLower === 'free';
        const isTestSong = titleLower === 'test' && artistLower === 'test';
        
        // Parse price - handle both string and number inputs
        let priceNum;
        const priceValue = updated.price;
        
        // Handle empty/null/undefined
        if (priceValue === '' || priceValue === null || priceValue === undefined) {
          priceNum = NaN;
        } else {
          // Convert to string first to handle both "0.00" and 0
          const priceStr = String(priceValue).trim();
          if (priceStr === '' || priceStr === '0' || priceStr === '0.00' || priceStr === '0.0') {
            priceNum = 0;
          } else {
            priceNum = parseFloat(priceStr);
            if (isNaN(priceNum)) {
              priceNum = NaN;
            }
          }
        }
        
        // FREE SONG CHECK - This must come FIRST and be most permissive
        if (isFreeSong) {
          // For free songs, allow 0, 0.00, empty (while typing), or any representation of zero
          if (priceNum === 0 || priceValue === 0 || priceValue === '0' || priceValue === '0.00' || priceValue === '0.0' || priceValue === '' || isNaN(priceNum)) {
            setPriceError(''); // Valid - free songs can be £0.00
          } else if (priceNum > 0) {
            setPriceError(`For "free" songs, price must be ${zeroLabel}.`);
          } else if (priceNum < 0) {
            setPriceError('Price cannot be negative.');
          } else {
            setPriceError(''); // Default to no error for free songs
          }
          return updated;
        }
        
        // TEST SONG CHECK
        if (isTestSong) {
          if (isNaN(priceNum)) {
            setPriceError('');
          } else if (priceNum < 0) {
            setPriceError('Price cannot be negative.');
          } else if (priceNum < testMin) {
            setPriceError(`Minimum price for test songs is ${testLabel}.`);
          } else {
            setPriceError('');
          }
          return updated;
        }
        
        // REGULAR SONG VALIDATION
        if (isNaN(priceNum)) {
          setPriceError(''); // Allow empty while typing
        } else if (priceNum < 0) {
          setPriceError('Price cannot be negative.');
        } else if (priceNum > 0 && priceNum < minRegular) {
          setPriceError(`Minimum price is ${minLabel}. We recommend pricing close to your region's minimum for better earnings.`);
        } else if (priceNum >= minRegular) {
          setPriceError('');
        } else {
          setPriceError(''); // Allow 0 while typing (will be validated on save)
        }
      }
      
      return updated;
    });
  };

  const handleSave = () => {
    // Get and normalize values
    const titleLower = (formData.title || '').trim().toLowerCase();
    const artistLower = (formData.artist || '').trim().toLowerCase();
    const isFreeSong = titleLower === 'free' && artistLower === 'free';
    const isTestSong = titleLower === 'test' && artistLower === 'test';
    
    // Parse price - handle both string and number, including "0.00"
    let price;
    const priceValue = formData.price;
    
    if (priceValue === '' || priceValue === null || priceValue === undefined) {
      price = NaN;
    } else {
      // Convert to string first to handle both "0.00" and 0
      const priceStr = String(priceValue).trim();
      if (priceStr === '' || priceStr === '0' || priceStr === '0.00' || priceStr === '0.0') {
        price = 0;
      } else {
        price = parseFloat(priceStr);
        if (isNaN(price)) {
          price = NaN;
        }
      }
    }
    
    // Validate required fields
    if (!formData.title.trim() || !formData.artist.trim()) {
      setPriceError('Please fill in both title and artist.');
      return;
    }
    
    // FREE SONG VALIDATION - Check this FIRST
    if (isFreeSong) {
      // For free songs, explicitly allow 0, "0", "0.00", "0.0", etc.
      if (price === 0 || priceValue === 0 || priceValue === '0' || priceValue === '0.00' || priceValue === '0.0') {
        // Valid - proceed to save
        setPriceError('');
      } else if (isNaN(price)) {
        setPriceError('Please enter a price.');
        return;
      } else if (price < 0) {
        setPriceError('Price cannot be negative.');
        return;
      } else if (price > 0) {
        setPriceError(`For "free" songs, price must be ${zeroLabel}.`);
        return;
      }
      // If we get here, price is 0, which is valid
    } 
    // Test song validation (USD: $1, other currencies: e.g. £0.50)
    else if (isTestSong) {
      if (isNaN(price)) {
        setPriceError('Please enter a price.');
        return;
      }
      if (price < 0) {
        setPriceError('Price cannot be negative.');
        return;
      }
      if (price < testMin) {
        setPriceError(`Minimum price for test songs is ${testLabel}.`);
        return;
      }
    } 
    // Regular song validation
    else {
      if (isNaN(price)) {
        setPriceError('Please enter a price.');
        return;
      }
      if (price < 0) {
        setPriceError('Price cannot be negative.');
        return;
      }
      if (price < minRegular) {
        setPriceError(`Minimum price is ${minLabel}. We recommend pricing close to your region's minimum for better earnings.`);
        return;
      }
    }
    
    // Clear any error if we get here
    setPriceError('');
    
    const songData = {
      title: formData.title.trim(),
      original_artist: formData.artist.trim(), // Use original_artist field
      price: price,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      archived: false
    };
    onSave(songData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-800 rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-4">Add New Song</h3>
        
        <div className="space-y-4">
          <Input
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Song Title"
            className="bg-gray-700 border-gray-600 text-white"
          />
          <Input
            name="artist"
            value={formData.artist}
            onChange={handleInputChange}
            placeholder="Artist Name"
            className="bg-gray-700 border-gray-600 text-white"
          />
          <div className="space-y-2">
            <label className="text-sm text-gray-300">
              Price ({sym}) *
            </label>
            <Input
              name="price"
              type="number"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={handleInputChange}
              placeholder={String(minRegular)}
              className={`bg-gray-700 border-gray-600 text-white ${
                priceError ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
            <p className="text-xs text-gray-400 mt-1">
              Minimum: {minLabel}. Use "free"/"free" for {zeroLabel} or "test"/"test" for {testLabel}.
            </p>
            {priceError && (
              <p className="text-sm text-red-400 mt-1 flex items-start">
                <span className="mr-1">⚠️</span>
                <span>{priceError}</span>
              </p>
            )}
            {!priceError && formData.price !== '' && formData.price !== undefined && (
              (formData.title.trim().toLowerCase() === 'free' && formData.artist.trim().toLowerCase() === 'free' && parseFloat(formData.price) === 0) ||
              (formData.title.trim().toLowerCase() === 'test' && formData.artist.trim().toLowerCase() === 'test' && parseFloat(formData.price) >= testMin) ||
              (parseFloat(formData.price) >= minRegular)
            ) && (
              <p className="text-xs text-green-400 mt-1">✓ Price meets minimum requirement</p>
            )}
          </div>
          <Input
            name="tags"
            value={formData.tags}
            onChange={handleInputChange}
            placeholder="Tags (comma separated)"
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>
        
        <div className="flex space-x-3 mt-6">
          <Button 
            onClick={handleSave} 
            className="bg-green-600 hover:bg-green-700 flex-1"
            disabled={!!priceError}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Song
          </Button>
          <Button onClick={onCancel} variant="outline" className="flex-1">
            Cancel
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Add Gig Modal Component
const AddGigModal = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    venue_name: '',
    date: '',
    time: '',
    address: '',
    postcode: '',
    city: '',
    rate: 0
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!formData.date || !formData.venue_name) return;
    const gigData = {
      ...formData,
      // Keep legacy 'venue' alias so admin display still works
      venue: formData.venue_name,
      location: [formData.address, formData.postcode, formData.city].filter(Boolean).join(', '),
      rate: parseFloat(formData.rate) || 0
    };
    onSave(gigData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-800 rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-4">Add New Gig</h3>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Gig / Event Title</label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g. Live at The Red Lion"
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Venue Name <span className="text-red-400">*</span></label>
            <Input
              name="venue_name"
              value={formData.venue_name}
              onChange={handleInputChange}
              placeholder="e.g. The Red Lion"
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Date <span className="text-red-400">*</span></label>
              <Input
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Start Time</label>
              <Input
                name="time"
                type="time"
                value={formData.time}
                onChange={handleInputChange}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Street Address</label>
            <Input
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="e.g. 123 High Street"
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Postcode</label>
              <Input
                name="postcode"
                value={formData.postcode}
                onChange={handleInputChange}
                placeholder="e.g. SW1A 1AA"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">City</label>
              <Input
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="e.g. London"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 bg-gray-900/50 rounded p-2">
            📍 Address details are used so audiences can find the event on the map at /find-performer. The more complete the address, the better.
          </p>
        </div>

        <div className="flex space-x-3 mt-6">
          <Button
            onClick={handleSave}
            disabled={!formData.date || !formData.venue_name}
            className="bg-green-600 hover:bg-green-700 flex-1 disabled:opacity-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Gig
          </Button>
          <Button onClick={onCancel} variant="outline" className="flex-1">
            Cancel
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminArtistManager;
